import * as jose from "jose";
import type { UpdateSettings } from "../components/UpdateExistingRecordsButton/types";
import type { ApplicationModel } from "../types/application.model";
import type { UserPreferences } from "../types/user-preferences";

interface SmartSuiteParams {
  accountId: string;
  applicationId: string;
  userPreferences: UserPreferences;
  role: string;
  memberId: string;
  recordsLimit: number | undefined;
  isSolutionOwner: boolean;
  featureFlags: Record<string, boolean>;
}

interface CreateTemplateResponse {
  templateKey: string;
  apiToken: string;
  uploadUrl: string;
}

export class OneSchema {
  private readonly microServiceUrl: string =
    process.env.REACT_APP_ONESCHEMA_MICROSERVICE_URL ?? "";

  async createTemplate(params: {
    accountId: string;
    jwtToken: string;
    applicationId: string | undefined;
    accessToken: string | undefined;
    application?: ApplicationModel;
  }): Promise<CreateTemplateResponse> {
    const jsonResponse = await fetch(
      `${this.microServiceUrl}/smartsuite.oneschema_engine/create-template`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "account-id": params.accountId,
        },
        body: JSON.stringify(params),
        credentials: "include",
      },
    );

    // A failed request (e.g. a 500 from the engine) returns an empty body, so
    // parsing it would silently yield an object with no templateKey and the
    // importer would render a blank modal. Surface the failure instead.
    if (!jsonResponse.ok) {
      throw new Error(
        `create-template request failed with status ${jsonResponse.status}`,
      );
    }

    return await jsonResponse.json();
  }

  async saveUpdateSettings(
    accountId: string,
    templateKey: string,
    updateSettings?: UpdateSettings,
  ): Promise<void> {
    const jsonResponse = await fetch(
      `${this.microServiceUrl}/smartsuite.oneschema_engine/save-update-settings`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "account-id": accountId,
        },
        body: JSON.stringify({
          templateKey,
          updateSettings,
        }),
        credentials: "include",
      },
    );

    return await jsonResponse.json();
  }

  async makeJwt(params: SmartSuiteParams): Promise<string> {
    return await new jose.SignJWT({
      iss: process.env.REACT_APP_ONESCHEMA_CLIENT_ID ?? "",
      metadata: {
        account_id: params.accountId,
        application_id: params.applicationId,
        userPreferences: params.userPreferences,
        role: params.role,
        member_id: params.memberId,
        recordsLimit: params.recordsLimit,
        isSolutionOwner: params.isSolutionOwner,
        featureFlags: params.featureFlags,
      },
    })
      .setProtectedHeader({ alg: "HS256" })
      .sign(
        new TextEncoder().encode(
          process.env.REACT_APP_ONESCHEMA_CLIENT_SECRET ?? "",
        ),
      );
  }

  async getApplications(
    solutionId: string,
    accountId: string,
  ): Promise<ApplicationModel[]> {
    const jsonResponse = await fetch(
      `${this.microServiceUrl}/smartsuite.oneschema_engine/applications/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "account-id": accountId,
        },
        body: JSON.stringify({
          solutionId,
          accountId,
        }),
        credentials: "include",
      },
    );

    return await jsonResponse.json().then((response) => {
      if (!response.length) {
        throw new Error("Bad response - missing appliations list");
      }
      return response;
    });
  }
}
